import { fmtSASTDateTime, fmtSASTDate } from "./api";
// CSV + PDF (via print) helpers for voucher exports.

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => csvEscape(r[h])).join(",")),
  ];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

function fmtZAR(n) {
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(n || 0);
}

/**
 * Open a new window with a print-friendly batch sheet and trigger
 * the browser's print dialog (user picks "Save as PDF").
 *
 * @param {string} title — heading shown on the sheet
 * @param {Array<{pin, amount, expires_at, service, status, id}>} vouchers
 * @param {{showFullPin: boolean, merchantName?: string, businessAddress?: string}} opts
 */
export function printVoucherBatch(title, vouchers, opts = {}) {
  const showFull = !!opts.showFullPin;
  const merchant = opts.merchantName || "FlexiVouch Merchant";
  const addr = opts.businessAddress || "31 5th Street, Commercia, Midrand, 1685";
  const generatedAt = fmtSASTDateTime();

  const rows = vouchers
    .map(
      (v, i) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="mono pin">${showFull ? (v.pin || "") : (v.pin_masked || "")}</td>
        <td class="num">${fmtZAR(v.amount ?? v.balance ?? v.original_amount ?? 0)}</td>
        ${
          showFull
            ? ""
            : `<td class="num">${fmtZAR(v.balance ?? 0)}</td><td>${(v.status || "").replace("_", " ")}</td>`
        }
        <td>${(v.service || v.intended_service || "general").toUpperCase()}</td>
        <td>${new Date(v.expires_at || Date.now()).toLocaleDateString("en-ZA")}</td>
      </tr>`
    )
    .join("");

  const totalIssued = vouchers.reduce(
    (s, v) => s + Number(v.amount ?? v.original_amount ?? 0),
    0
  );
  const totalBalance = vouchers.reduce(
    (s, v) => s + Number(v.balance ?? v.amount ?? 0),
    0
  );

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${title}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Plus Jakarta Sans", "Segoe UI", sans-serif; color: #0A2540; margin: 0; padding: 0; }
  .head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; border-bottom: 3px solid #0A2540; padding-bottom: 12px; margin-bottom: 18px; }
  .brand { font-family: "Outfit", system-ui, sans-serif; font-size: 26px; font-weight: 800; letter-spacing: -0.02em; }
  .brand span { color: #00C4B4; }
  .meta { font-size: 11px; color: #555; line-height: 1.5; text-align: right; }
  .kicker { font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; color: #00C4B4; font-weight: 700; margin-bottom: 2px; }
  h1 { font-family: "Outfit", system-ui, sans-serif; font-size: 22px; margin: 0 0 14px; letter-spacing: -0.02em; }
  table { width:100%; border-collapse: collapse; font-size: 12px; }
  th, td { padding: 8px 10px; border-bottom: 1px solid #E5E7EB; text-align: left; }
  th { background: #F8F9FA; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: #555; font-weight: 700; }
  .mono { font-family: "JetBrains Mono", ui-monospace, monospace; }
  .pin { letter-spacing: 0.06em; color: #0A2540; font-weight: 600; }
  .num { font-family: "JetBrains Mono", ui-monospace, monospace; }
  .totals { margin-top: 14px; display:flex; gap: 24px; font-size: 12px; }
  .totals strong { display:block; font-size: 18px; font-family: "Outfit", system-ui, sans-serif; color: #0A2540; }
  .note { margin-top: 26px; font-size: 10px; color: #888; line-height: 1.5; border-top: 1px dashed #ccc; padding-top: 10px; }
  .warn { background: #FFF4E5; border: 1px solid #FFD8A8; padding: 10px 12px; border-radius: 8px; font-size: 11px; color: #7a4500; margin-bottom: 16px; }
</style>
</head>
<body>
  <div class="head">
    <div>
      <p class="kicker">FlexiVouch · Batch sheet</p>
      <p class="brand">Flexi<span>Vouch</span></p>
    </div>
    <div class="meta">
      Issued by ${merchant}<br />
      ${addr}<br />
      Generated ${generatedAt}<br />
      ${vouchers.length} voucher${vouchers.length === 1 ? "" : "s"}
    </div>
  </div>

  <h1>${title}</h1>

  ${
    showFull
      ? '<div class="warn"><strong>⚠ Confidential — PINs are shown only once.</strong> Distribute securely. Lost PINs cannot be recovered.</div>'
      : ""
  }

  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>PIN</th>
        <th>Value</th>
        ${showFull ? "" : "<th>Balance</th><th>Status</th>"}
        <th>Service</th>
        <th>Expires</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="totals">
    <div>Total issued <strong>${fmtZAR(totalIssued)}</strong></div>
    ${showFull ? "" : `<div>Outstanding <strong>${fmtZAR(totalBalance)}</strong></div>`}
  </div>

  <p class="note">
    Generated by FlexiVouch — a brand of Praxio (Pty) Ltd. Vouchers expire 3 years from issuance unless otherwise stated.
    Tel 079 485 2928 · info@praxio.co.za
  </p>

  <script>
    window.addEventListener("load", function() {
      setTimeout(function() { window.print(); }, 250);
    });
  </script>
</body>
</html>`;

  const w = window.open("", "_blank");
  if (!w) {
    alert("Pop-up blocked — please allow pop-ups to export PDF");
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
}
